import type { FC, KeyboardEvent, MouseEvent, PointerEvent, ReactNode } from 'react'
import type { FooterLink } from '@/constants/content'

import { useEffect, useRef, useState } from 'react'
import { ArrowRight, ArrowUpRight, Rotate3d } from 'lucide-react'
import { cn } from '@/lib/utils'
import { engine } from '@/lib/motion/engine'
import { openRequest } from '@/lib/app-store'
import { live } from '@/lib/drone/control'
import { Bracket } from '@/components/landing/primitives'
import { footer, footerColumns } from '@/constants/content'

const linkClass = 'group inline-flex items-center gap-1.5 text-[15px] tracking-[-0.01em] text-ink transition-transform duration-300 ease-out hover:translate-x-1 focus-visible:translate-x-1'

const LinkArrow: FC = (): ReactNode => {
    return (
        <ArrowRight
            aria-hidden='true'
            className='size-3.5 -translate-x-1 opacity-0 transition-all duration-300 ease-out group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100'
        />
    )
}

const FooterItem: FC<{ link: FooterLink }> = ({ link }): ReactNode => {
    if (link.topic) {
        const topic = link.topic

        return (
            <button type='button' onClick={() => openRequest(topic)} className={linkClass}>
                {link.label}
                <LinkArrow />
            </button>
        )
    }

    const href = link.href ?? '#hero'

    const onClick = (event: MouseEvent<HTMLAnchorElement>): void => {
        const target = document.getElementById(href.replace('#', ''))

        if (!target) return

        event.preventDefault()
        engine.scrollTo(target, { duration: 2.8 })
    }

    return (
        <a href={href} onClick={onClick} className={linkClass}>
            {link.label}
            <LinkArrow />
        </a>
    )
}

interface DragPointer {
    id: number
    x: number
    y: number
    time: number
}

/**
 * Invisible surface over the drone's hover spot in the footer. Dragging it turns the
 * drone (the WebGL canvas itself never takes pointer events); the stage adds the coast
 * after release and eases the drone home when the footer scrolls away.
 */
const DroneTurntable: FC = (): ReactNode => {
    const surfaceRef = useRef<HTMLDivElement>(null)
    const pointerRef = useRef<DragPointer | null>(null)
    const [turned, setTurned] = useState(false)

    useEffect(() => {
        const surface = surfaceRef.current

        if (!surface) return

        const observer = new IntersectionObserver(([entry]) => {
            live.spin.active = entry.isIntersecting

            if (!entry.isIntersecting) {
                live.spin.dragging = false
                pointerRef.current = null
            }
        }, { threshold: 0.3 })

        observer.observe(surface)

        return () => {
            observer.disconnect()
            live.spin.active = false
            live.spin.dragging = false
        }
    }, [])

    const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
        if (event.pointerType === 'mouse' && event.button !== 0) return

        event.currentTarget.setPointerCapture(event.pointerId)
        pointerRef.current = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now() }
        live.spin.dragging = true
        live.spin.velocity = 0

        if (!turned) setTurned(true)
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
        const pointer = pointerRef.current

        if (!pointer || pointer.id !== event.pointerId) return

        const now = performance.now()
        const dt = Math.max(8, now - pointer.time) / 1000
        const turn = (event.clientX - pointer.x) * 0.012

        live.spin.yaw += turn
        live.spin.velocity = live.spin.velocity * 0.6 + (turn / dt) * 0.4

        if (event.pointerType === 'mouse') {
            live.spin.pitch = Math.min(0.5, Math.max(-0.5, live.spin.pitch + (event.clientY - pointer.y) * 0.006))
        }

        pointer.x = event.clientX
        pointer.y = event.clientY
        pointer.time = now
    }

    const release = (event: PointerEvent<HTMLDivElement>): void => {
        const pointer = pointerRef.current

        if (!pointer || pointer.id !== event.pointerId) return

        pointerRef.current = null
        live.spin.dragging = false

        /* Held still before letting go: no coast */
        if (performance.now() - pointer.time > 90) live.spin.velocity = 0
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

        event.preventDefault()
        live.spin.velocity += event.key === 'ArrowRight' ? 2.2 : -2.2

        if (!turned) setTurned(true)
    }

    return (
        <>
            <div
                ref={surfaceRef}
                role='application'
                aria-roledescription='drone viewer'
                aria-label={footer.dragLabel}
                tabIndex={0}
                data-cursor='hover'
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={release}
                onPointerCancel={release}
                onKeyDown={onKeyDown}
                className='absolute inset-x-[10%] bottom-14 top-14 touch-pan-y select-none rounded-sm outline-none focus-visible:ring-1 focus-visible:ring-ink/20'
            />

            <span
                aria-hidden='true'
                className={cn(
                    'label-mono pointer-events-none absolute bottom-12 left-1/2 inline-flex -translate-x-1/2 items-center gap-2 whitespace-nowrap text-haze transition-opacity duration-700 md:bottom-5',
                    turned ? 'opacity-0' : 'opacity-100'
                )}
            >
                <Rotate3d className='size-4' />
                {footer.drag}
            </span>
        </>
    )
}

const SiteFooter: FC = (): ReactNode => {
    return (
        <footer id='contact' aria-labelledby='footer-title' className='relative flex min-h-svh flex-col'>
            <div className='relative min-h-[44svh] flex-1'>
                <div aria-hidden='true' data-anchor='footer-stage' className='absolute inset-x-0 bottom-0 top-14' />

                <div aria-hidden='true' className='absolute inset-0 bg-[radial-gradient(48%_60%_at_50%_58%,rgba(255,255,255,0.95),rgba(255,255,255,0)_70%)]' />

                <DroneTurntable />

                <div className='absolute bottom-5 left-5 right-5 flex items-end justify-between text-haze md:left-10 md:right-10'>
                    <Bracket>Orvane L4</Bracket>
                    <span className='label-mono'>Cleared for flight</span>
                </div>
            </div>

            <div aria-hidden='true' className='mx-3 h-px bg-line md:mx-5' />

            <div className='grid gap-8 px-5 pt-9 md:grid-cols-2 md:gap-0 md:px-10 md:pt-12'>
                <h2 id='footer-title' className='max-w-[620px] text-[clamp(30px,3.9vw,64px)] font-light leading-[1.02] tracking-[-0.045em] text-ink md:pr-10'>
                    {footer.lead}
                </h2>

                <div className='flex flex-col items-start gap-6 md:pl-10'>
                    <p className='max-w-[420px] text-[clamp(16px,1.3vw,20px)] leading-[1.45] tracking-[-0.015em] text-haze'>
                        {footer.follow}
                    </p>

                    <button
                        type='button'
                        onClick={() => openRequest('survey')}
                        className='group inline-flex h-14 items-center gap-3 bg-ink px-7 text-[15px] tracking-[-0.01em] text-white transition-colors duration-300 hover:bg-[#2b3b44]'
                    >
                        {footer.cta}
                        <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                    </button>
                </div>
            </div>

            <div className='grid grid-cols-2 gap-x-6 gap-y-10 px-5 pb-8 pt-12 md:grid-cols-[1fr_auto_auto] md:items-end md:gap-x-24 md:px-10 md:pb-10 md:pt-16'>
                <p aria-hidden='true' className='col-span-2 select-none text-[clamp(72px,13vw,210px)] font-light leading-[0.78] tracking-[-0.075em] text-ink md:col-span-1'>
                    orvane
                </p>

                {footerColumns.map(column => (
                    <nav key={column.title} aria-label={column.title} className='flex flex-col gap-4 md:pb-3'>
                        <Bracket className='text-haze'>{column.title}</Bracket>

                        <ul className='flex flex-col gap-2'>
                            {column.links.map(link => (
                                <li key={link.label}>
                                    <FooterItem link={link} />
                                </li>
                            ))}
                        </ul>
                    </nav>
                ))}
            </div>

            <div className='flex flex-col gap-2 border-t border-line px-5 py-5 text-haze md:flex-row md:items-center md:justify-between md:px-10'>
                <span className='label-mono'>© 2026 Orvane Aerial Systems. All rights reserved.</span>
                <span className='label-mono'>Specifications are indicative and may change without notice.</span>
                <span className='label-mono'>Precision Demo v1.0</span>
            </div>
        </footer>
    )
}

export default SiteFooter
